import { useQuery } from '@provision/graphql-client';
import { GET_BACKGROUND_JOB_ITEMS_QUERY } from 'src/graphql/entities/background-job/background-job.operations';
import {
  BackgroundJobItem,
  BackgroundJobItemStatus,
  GetBackgroundJobItemsResult,
  GetBackgroundJobItemsVars,
} from 'src/graphql/entities/background-job/background-job.types';

const DEFAULT_PAGE_SIZE = 100;
const EMPTY_ITEMS: BackgroundJobItem[] = [];

/** Loads the items of a background job, optionally only those with a given status. */
export const useBackgroundJobItems = (input: {
  jobId: string | null | undefined;
  status?: BackgroundJobItemStatus;
  pageSize?: number;
  skip?: boolean;
}) => {
  const params = input.jobId
    ? {
        jobId: input.jobId,
        status: input.status,
        limit: input.pageSize ?? DEFAULT_PAGE_SIZE,
      }
    : undefined;

  const query = useQuery<GetBackgroundJobItemsResult, GetBackgroundJobItemsVars>(
    GET_BACKGROUND_JOB_ITEMS_QUERY,
    {
      skip: input.skip || !params,
      variables: params ? { params } : undefined,
      fetchPolicy: 'network-only',
      context: { ttl: 0 },
    },
  );

  const page = query.data?.getBackgroundJobItems;
  const nextToken = page?.nextToken ?? null;

  const loadMore = async () => {
    if (!nextToken || !params) {
      return;
    }

    await query.fetchMore({
      variables: { params: { ...params, nextToken } },
      updateQuery: (previous, { fetchMoreResult }) => ({
        getBackgroundJobItems: {
          ...fetchMoreResult.getBackgroundJobItems,
          items: [
            ...previous.getBackgroundJobItems.items,
            ...fetchMoreResult.getBackgroundJobItems.items,
          ],
        },
      }),
    });
  };

  return {
    items: page?.items ?? EMPTY_ITEMS,
    hasMore: Boolean(nextToken),
    loadMore,
    loading: query.loading,
    error: query.error,
  };
};
